 
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  DollarSign,
  Key,
  Gauge,
  Bell,
  ScrollText,
  HeadphonesIcon,
  Settings,
  LogOut,
  ChevronRight,
  Menu,
  X,
  Shield,
} from 'lucide-react';
import { cn } from '@innflow/ui';
import { useAuth } from '../providers/AuthProvider';
import { useTotalUnreadCount } from '../hooks/useUnreadCount';

interface MenuItem {
  label: string;
  icon: React.ElementType;
  path: string;
  badge?: number;
  color?: string;
}

const menuItems: MenuItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/', color: 'text-red-500' },
  { label: 'Clientes', icon: Users, path: '/clients', color: 'text-orange-500' },
  { label: 'Assinaturas', icon: CreditCard, path: '/subscriptions', color: 'text-amber-500' },
  { label: 'Receita', icon: DollarSign, path: '/revenue', color: 'text-emerald-500' },
  { label: 'API Keys', icon: Key, path: '/api-keys', color: 'text-violet-500' },
  { label: 'Limites', icon: Gauge, path: '/limits', color: 'text-blue-500' },
  { label: 'Alertas', icon: Bell, path: '/alerts', badge: 5, color: 'text-red-400' },
  { label: 'Logs', icon: ScrollText, path: '/logs', color: 'text-slate-400' },
  { label: 'Suporte', icon: HeadphonesIcon, path: '/support', color: 'text-cyan-500' },
];

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const totalUnread = useTotalUnreadCount();

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const userName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'Admin';

  return (
    <div className="min-h-screen bg-black">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-screen z-50 flex flex-col",
          "bg-[#0a0a0a] border-r border-white/[0.06]",
          "transition-all duration-300",
          isOpen ? "w-64" : "w-[72px]"
        )}
      >
        {/* Toggle Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="absolute top-4 right-4 w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5 transition-all z-10"
        >
          {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Logo */}
        <div className="flex items-center gap-3 p-4 h-20">
          <div className="flex-shrink-0 w-11 h-11 rounded-2xl admin-gradient flex items-center justify-center shadow-lg shadow-red-500/25">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div className={cn(
            "flex flex-col overflow-hidden transition-all duration-300",
            isOpen ? "opacity-100 w-auto" : "opacity-0 w-0"
          )}>
            <span className="text-lg font-bold admin-gradient-text whitespace-nowrap">INNFLOW</span>
            <span className="text-xs text-red-400 whitespace-nowrap font-medium">MASTER</span>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 px-3 py-4 space-y-2 overflow-y-auto">
          {/* Principal */}
          <div className={cn("space-y-1", !isOpen && "opacity-0 h-0 overflow-hidden")}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 mb-2">
              Principal
            </p>
            {menuItems.slice(0, 1).map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center gap-3 py-3 px-3 rounded-xl transition-all duration-200 group relative",
                    isActive
                      ? "bg-red-500/10 text-white"
                      : "text-slate-400 hover:bg-white/5 hover:text-white"
                  )}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-red-500 to-orange-500 rounded-r-full" />
                  )}
                  <div className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all",
                    isActive ? "bg-red-500/20" : "group-hover:bg-white/5"
                  )}>
                    <Icon className={cn("w-5 h-5", isActive ? item.color : "group-hover:text-white")} />
                  </div>
                  <span className={cn(
                    "flex-1 text-left text-sm font-medium whitespace-nowrap overflow-hidden transition-all",
                    isOpen ? "opacity-100 w-auto" : "opacity-0 w-0"
                  )}>
                    {item.label}
                  </span>
                  {isOpen && (
                    <ChevronRight className={cn(
                      "w-4 h-4 transition-all flex-shrink-0",
                      isActive ? "opacity-100 text-red-400" : "opacity-0 group-hover:opacity-50"
                    )} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Gestão de Clientes */}
          <div className={cn("space-y-1", !isOpen && "opacity-0 h-0 overflow-hidden")}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 mb-2">
              Gestão de Clientes
            </p>
            {menuItems.slice(1, 3).map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center gap-3 py-3 px-3 rounded-xl transition-all duration-200 group relative",
                    isActive
                      ? "bg-red-500/10 text-white"
                      : "text-slate-400 hover:bg-white/5 hover:text-white"
                  )}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-red-500 to-orange-500 rounded-r-full" />
                  )}
                  <div className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all",
                    isActive ? "bg-red-500/20" : "group-hover:bg-white/5"
                  )}>
                    <Icon className={cn("w-5 h-5", isActive ? item.color : "group-hover:text-white")} />
                  </div>
                  <span className={cn(
                    "flex-1 text-left text-sm font-medium whitespace-nowrap overflow-hidden transition-all",
                    isOpen ? "opacity-100 w-auto" : "opacity-0 w-0"
                  )}>
                    {item.label}
                  </span>
                  {isOpen && (
                    <ChevronRight className={cn(
                      "w-4 h-4 transition-all flex-shrink-0",
                      isActive ? "opacity-100 text-red-400" : "opacity-0 group-hover:opacity-50"
                    )} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Financeiro */}
          <div className={cn("space-y-1", !isOpen && "opacity-0 h-0 overflow-hidden")}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 mb-2">
              Financeiro
            </p>
            {menuItems.slice(3, 4).map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center gap-3 py-3 px-3 rounded-xl transition-all duration-200 group relative",
                    isActive
                      ? "bg-red-500/10 text-white"
                      : "text-slate-400 hover:bg-white/5 hover:text-white"
                  )}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-red-500 to-orange-500 rounded-r-full" />
                  )}
                  <div className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all",
                    isActive ? "bg-red-500/20" : "group-hover:bg-white/5"
                  )}>
                    <Icon className={cn("w-5 h-5", isActive ? item.color : "group-hover:text-white")} />
                  </div>
                  <span className={cn(
                    "flex-1 text-left text-sm font-medium whitespace-nowrap overflow-hidden transition-all",
                    isOpen ? "opacity-100 w-auto" : "opacity-0 w-0"
                  )}>
                    {item.label}
                  </span>
                  {isOpen && (
                    <ChevronRight className={cn(
                      "w-4 h-4 transition-all flex-shrink-0",
                      isActive ? "opacity-100 text-red-400" : "opacity-0 group-hover:opacity-50"
                    )} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Suporte & Operações */}
          <div className={cn("space-y-1", !isOpen && "opacity-0 h-0 overflow-hidden")}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 mb-2">
              Suporte & Operações
            </p>
            {menuItems.slice(4, 6).map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center gap-3 py-3 px-3 rounded-xl transition-all duration-200 group relative",
                    isActive
                      ? "bg-red-500/10 text-white"
                      : "text-slate-400 hover:bg-white/5 hover:text-white"
                  )}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-red-500 to-orange-500 rounded-r-full" />
                  )}
                  <div className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all",
                    isActive ? "bg-red-500/20" : "group-hover:bg-white/5"
                  )}>
                    <Icon className={cn("w-5 h-5", isActive ? item.color : "group-hover:text-white")} />
                  </div>
                  <span className={cn(
                    "flex-1 text-left text-sm font-medium whitespace-nowrap overflow-hidden transition-all",
                    isOpen ? "opacity-100 w-auto" : "opacity-0 w-0"
                  )}>
                    {item.label}
                  </span>
                  {/* Badge dinâmico para Suporte */}
                  {item.path === '/support' && totalUnread > 0 && (
                    <span className={cn(
                      "flex-shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center",
                      isOpen ? "opacity-100" : "absolute top-1 right-1 scale-75"
                    )}>
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  )}
                  {item.badge && item.path !== '/support' && (
                    <span className={cn(
                      "flex-shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center",
                      isOpen ? "opacity-100" : "absolute top-1 right-1 scale-75"
                    )}>
                      {item.badge}
                    </span>
                  )}
                  {isOpen && (
                    <ChevronRight className={cn(
                      "w-4 h-4 transition-all flex-shrink-0",
                      isActive ? "opacity-100 text-red-400" : "opacity-0 group-hover:opacity-50"
                    )} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Configurações & Sistema */}
          <div className={cn("space-y-1", !isOpen && "opacity-0 h-0 overflow-hidden")}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 mb-2">
              Configurações & Sistema
            </p>
            {menuItems.slice(6).map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center gap-3 py-3 px-3 rounded-xl transition-all duration-200 group relative",
                    isActive
                      ? "bg-red-500/10 text-white"
                      : "text-slate-400 hover:bg-white/5 hover:text-white"
                  )}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-red-500 to-orange-500 rounded-r-full" />
                  )}
                  <div className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all",
                    isActive ? "bg-red-500/20" : "group-hover:bg-white/5"
                  )}>
                    <Icon className={cn("w-5 h-5", isActive ? item.color : "group-hover:text-white")} />
                  </div>
                  <span className={cn(
                    "flex-1 text-left text-sm font-medium whitespace-nowrap overflow-hidden transition-all",
                    isOpen ? "opacity-100 w-auto" : "opacity-0 w-0"
                  )}>
                    {item.label}
                  </span>
                  {isOpen && (
                    <ChevronRight className={cn(
                      "w-4 h-4 transition-all flex-shrink-0",
                      isActive ? "opacity-100 text-red-400" : "opacity-0 group-hover:opacity-50"
                    )} />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Bottom Section */}
        <div className="px-3 pb-4 space-y-2">
          <div className="h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent my-4" />

          {/* Settings */}
          <button
            onClick={() => navigate('/settings')}
            className={cn(
              "w-full flex items-center gap-3 py-3 px-3 rounded-xl transition-all",
              location.pathname === '/settings'
                ? "bg-red-500/10 text-white"
                : "text-slate-400 hover:bg-white/5 hover:text-white"
            )}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5" />
            </div>
            <span className={cn(
              "text-sm font-medium whitespace-nowrap transition-all",
              isOpen ? "opacity-100" : "opacity-0 w-0"
            )}>
              Configurações
            </span>
          </button>

          {/* User Profile */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-red-500/5 border border-red-500/20">
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 rounded-lg admin-gradient flex items-center justify-center text-white font-bold">
                {userName.charAt(0).toUpperCase()}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#0a0a0a]" />
            </div>

            <div className={cn(
              "flex-1 overflow-hidden transition-all",
              isOpen ? "opacity-100 w-auto" : "opacity-0 w-0"
            )}>
              <p className="text-sm font-medium text-white truncate">{userName}</p>
              <p className="text-xs text-red-400 font-medium">Super Admin</p>
            </div>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 py-3 px-3 rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center">
              <LogOut className="w-5 h-5" />
            </div>
            <span className={cn(
              "text-sm font-medium whitespace-nowrap transition-all",
              isOpen ? "opacity-100" : "opacity-0 w-0"
            )}>
              Sair
            </span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "min-h-screen transition-all duration-300",
        isOpen ? "ml-64" : "ml-[72px]"
      )}>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

